import { action } from '@ember/object';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { formatNumber, hasUsFormatSeparators, switchFormatSeparators } from 'webapp/utils/number';

import FormInputComponent from 'webapp/components/form/input';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

interface Example {
	format: string;
	output: string;
}

const learnMoreUrl = 'https://help.databox.com/change-number-format-and-scale';

export default class MetricsPopupCustomFormatComponent extends PopupInnerModalComponent<PopupData.Form.CustomFormat> {
	@service('account') declare accountService: Service.Account;

	@tracked format = '';

	@cached
	get examples(): Example[] {
		const number = 1234;
		return ['0.0%', '#,###.00', '€ #,###.##'].map((format) => {
			return {
				format: this.accountService.isEuropeanNumberFormat
					? switchFormatSeparators(format)
					: format,
				output: formatNumber(number, format),
			};
		});
	}

	get usNotationSuggestion(): string | null {
		return this.accountService.isEuropeanNumberFormat && hasUsFormatSeparators(this.format)
			? switchFormatSeparators(this.format)
			: null;
	}

	get isSaveDisabled() {
		return !this.format || !!this.usNotationSuggestion;
	}

	get showLearnMoreLink() {
		return !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
	}

	@action
	saveFormat() {
		this.popupData.onSave(
			this.accountService.isEuropeanNumberFormat
				? switchFormatSeparators(this.format)
				: this.format,
		);
		this.close();
	}
	<template>
		<div class="title">Custom number format</div>
		<div class="content">
			<p>
				You can use custom formatting for numbers. Type in your format
				{{~#if this.showLearnMoreLink}}
					or
					<a class="learn-more" href={{learnMoreUrl}} target="_blank">learn more</a>
				{{~/if~}}
				.
			</p>
			{{#if this.accountService.isEuropeanNumberFormat}}
				<p>
					Use your account's number format: a comma for decimals and a dot for thousands,
					e.g. '<strong>#.###,00</strong>'.
				</p>
			{{/if}}
			<FormInputComponent
				id="format"
				name="format"
				@value={{this.format}}
				required={{true}}
			/>
			{{#if this.usNotationSuggestion}}
				<p class="warning-message">
					Use a comma for decimals and a dot for thousands. Did you mean
					<strong>{{this.usNotationSuggestion}}</strong>?
				</p>
			{{/if}}
			<div>Examples</div>
			<ul>
				{{#each this.examples as |example|}}
					<li>
						Format: '<strong>{{example.format}}</strong>' will output as:
						<strong>{{example.output}}</strong>
					</li>
				{{/each}}

			</ul>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@onTrigger={{this.saveFormat}}
						@submitInProgress={{this.submitInProgress}}
						@isDisabled={{this.isSaveDisabled}}
					/>
				</div>
			</div>
		</div>
	</template>
}
